import { ERROR_CODES } from '@widergy/web-utils';

import { actions as authActions } from 'redux/authHandlers';
import { actions as tipActions } from 'redux/tipHandlers';
import { actions as voucherActions } from 'redux/voucherHandlers';
import { actions as paymentActions } from 'redux/paymentHandlers';
import { actions as accountActions } from 'redux/accountHandlers';
import { actions as userFormActions } from 'redux/userFormHandlers';
import { actions as notificationActions } from 'redux/notificationHandlers';
import { actions as customProceduresActions } from 'redux/customProceduresHandlers';
import BillActions from 'redux/bill/actions';
import surveyActions from 'redux/survey/actions';
import store from 'redux/store';
import { appConfig } from 'config/customization';

import { categories, actions, labels, TOKEN_EXPIRATION, USER_SIGNOUT, EQUIFAX, OPEN_SGC } from './analytics';
import { UTILITIES } from './utilitiesMappers';

const FORMULARIO = 'formulario';
const WITHOUT_LOGIN = 'sin login';

const event = (category, action, label, value) => {
  const userId =
    (store.getState().auth.currentUser && store.getState().auth.currentUser.id) ||
    store.getState().auth.publicUserId;
  return {
    hitType: 'event',
    eventCategory: category,
    eventAction: action,
    eventLabel: label,
    eventValue: value,
    userId
  };
};

const recoveryPasswordRequestSuccess = () =>
  event(
    categories.REGISTRACIONES,
    actions.SOLICITAR_RECUPERO_CONTRASEÑA,
    labels.SOLICITAR_RECUPERO_CONTRASEÑA_OK
  );

const recoveryPasswordSuccess = () =>
  event(categories.REGISTRACIONES, actions.RECUPERAR_CONTRASEÑA, labels.RECUPERAR_CONTRASEÑA_OK);

const changePasswordSuccess = () =>
  event(categories.REGISTRACIONES, actions.CAMBIAR_CONTRASEÑA, labels.CAMBIAR_CONTRASEÑA_OK);

const signUpSuccess = () =>
  event(categories.REGISTRACIONES, actions.REGISTRO_USUARIO, labels.REGISTRO_USUARIO_OK);

const validateEmailSuccess = () => {
  const events = [];

  events.push(event(categories.REGISTRACIONES, actions.VALIDACION_USUARIO, labels.VALIDACION_USUARIO_OK));

  if (appConfig.analytics.trackDigitalBillOnRegister)
    events.push(
      event(categories.FACTURACION, actions.ADHESION_FACTURA_DIGITAL, labels.ADHESION_FACTURA_DIGITAL_OK)
    );

  return events;
};

const signInSuccess = () => event(categories.REGISTRACIONES, actions.INICIO_SESION, labels.INICIO_SESION_OK);

const signInFailure = action => {
  const error =
    action.payload === ERROR_CODES.INVALID_CREDENTIALS
      ? labels.INICIO_SESION_INCORRECTO_CREDENCIALES
      : action.payload === ERROR_CODES.NOT_CONFIRMED
      ? labels.INICIO_SESION_INCORRECTO_NO_CONFIRMADO
      : action.payload === ERROR_CODES.NETWORK_ERROR
      ? labels.INICIO_SESION_INCORRECTO_CONEXION
      : labels.INICIO_SESION_INCORRECTO;

  return event(categories.REGISTRACIONES, actions.INICIO_SESION_INCORRECTO, error);
};

const signOut = action => {
  const actionType = {};
  if (action.payload === TOKEN_EXPIRATION) {
    actionType.action = actions.EXPIRACION_SESION;
    actionType.label = labels.EXPIRACION_SESION_OK;
  }
  if (action.payload === USER_SIGNOUT) {
    actionType.action = actions.CIERRE_SESION;
    actionType.label = labels.CIERRE_SESION_OK;
  }

  if (actionType.action && actionType.label)
    return event(categories.REGISTRACIONES, actionType.action, actionType.label);

  return null;
};

const getIdentityValidationFormSuccess = action => {
  let labelProvider;
  if (
    action.payload &&
    action.payload.validation_questions &&
    action.payload.validation_questions.provider === EQUIFAX
  )
    labelProvider = labels.INGRESO_PREGUNTAS_VALIDACION_IDENTIDAD_EQUIFAX;
  if (
    action.payload &&
    action.payload.validation_questions &&
    action.payload.validation_questions.provider === OPEN_SGC
  )
    labelProvider = labels.INGRESO_PREGUNTAS_VALIDACION_IDENTIDAD_OPEN_SGC;

  return event(categories.REGISTRACIONES, actions.INGRESO_PREGUNTAS_VALIDACION_IDENTIDAD, labelProvider);
};

const validateIdentityAnswersSuccess = () =>
  event(
    categories.REGISTRACIONES,
    actions.PREGUNTAS_VALIDACION_IDENTIDAD,
    labels.PREGUNTAS_VALIDACION_IDENTIDAD_OK
  );

const validateIdentityAnswersFailure = () =>
  event(
    categories.REGISTRACIONES,
    actions.PREGUNTAS_VALIDACION_IDENTIDAD_ERROR,
    labels.PREGUNTAS_RELACION_CUENTA_NOT_OK
  );

const deleteAccountSuccess = () =>
  event(categories.REGISTRACIONES, actions.DESVINCULAR_SUMINISTRO, labels.DESVINCULAR_SUMINISTRO_OK);

const getPublicPaymentDocumentSuccess = () =>
  event(categories.COBRANZAS, actions.DESCARGA_CUPON_PAGO_SIN_LOGIN, labels.DESCARGA_CUPON_PAGO_SIN_LOGIN_OK);

const getPaymentDocumentSuccess = action => {
  const actionType = action.isPublic ? actions.DESCARGA_CUPON_PAGO_SIN_LOGIN : actions.DESCARGA_CUPON_PAGO;
  const labelType = action.isPublic ? labels.DESCARGA_CUPON_PAGO_SIN_LOGIN_OK : labels.DESCARGA_CUPON_PAGO_OK;
  return event(categories.COBRANZAS, actionType, labelType);
};

const getMultiplePaymentDocumentsSuccess = () =>
  event(categories.COBRANZAS, actions.DESCARGA_MASIVA_CUPON_PAGO, labels.DESCARGA_MASIVA_CUPON_PAGO_OK);

const paymentRequestFailure = action => {
  const actionType = action.isPublic
    ? actions.GENERACION_PAGO_ERRONEO_SIN_LOGIN
    : actions.GENERACION_PAGO_ERRONEO;
  const labelType = action.isPublic ? labels.GENERACION_PAGO_SIN_LOGIN_ERROR : labels.GENERACION_PAGO_ERROR;
  return event(
    categories.COBRANZAS,
    actionType,
    `${labelType} ${action.payload}`,
    Math.round(action.totalAmount)
  );
};

const paymentRequestSuccess = action => {
  const actionType = action.isPublic ? actions.GENERACION_PAGO_SIN_LOGIN : actions.GENERACION_PAGO;
  const labelType = action.isPublic ? labels.GENERACION_PAGO_SIN_LOGIN_OK : labels.GENERACION_PAGO_OK;
  return event(categories.COBRANZAS, actionType, labelType, Math.round(action.totalAmount));
};

const cancelationSuccess = action => {
  if (action.formType === 'claim') {
    return event(
      categories.RECLAMOS,
      actions.CANCELAR_RECLAMO,
      `${labels.CANCELAR_RECLAMO_OK} ${action.formName}`
    );
  }
  if (action.formType === 'procedure') {
    return event(
      categories.TRAMITES,
      `${actions.CANCELAR_TRAMITE} ${action.formName}`,
      labels.CANCELAR_TRAMITE_OK
    );
  }
  return null;
};

const reiterationSuccess = action => {
  if (action.formType === 'claim') {
    return event(categories.RECLAMOS, actions.REITERAR_RECLAMO, labels.REITERAR_RECLAMO_OK);
  }
  if (action.formType === 'procedure') {
    return event(
      categories.TRAMITES,
      `${actions.REITERAR_TRAMITE} ${action.formName}`,
      labels.REITERAR_TRAMITE_OK
    );
  }
  return null;
};

const submitFormSuccess = action => {
  if (action.payload.complaint)
    return event(
      categories.DENUNCIAS,
      `${
        action.payload.isPublic
          ? actions.ENVIAR_FORMULARIO_DENUNCIA_SIN_LOGIN
          : actions.ENVIAR_FORMULARIO_DENUNCIA
      } ${FORMULARIO}`,
      action.payload.isPublic
        ? labels.ENVIAR_FORMULARIO_DENUNCIA_SIN_LOGIN_OK
        : labels.ENVIAR_FORMULARIO_DENUNCIA_OK
    );

  if (action.payload.formType === 'procedureSurvey' || action.payload.formType === 'paymentSurvey') {
    return event(categories.ENCUESTAS, actions.GENERACION_DE_ENCUESTA);
  }
  if (action.payload.formType === 'claim') {
    return event(
      categories.RECLAMOS,
      action.payload.isPublic ? actions.GENERACION_RECLAMO_SIN_LOGIN : actions.GENERACION_RECLAMO,
      `${action.payload.isPublic ? labels.GENERACION_RECLAMO_SIN_LOGIN_OK : labels.GENERACION_RECLAMO_OK} ${
        action.payload.formName
      }`
    );
  }
  if (action.payload.formType === 'procedure') {
    if (appConfig.mappers === UTILITIES.edelap)
      return event(
        categories.FACTURACION,
        actions.ADHESION_FACTURA_DIGITAL,
        labels.ADHESION_FACTURA_DIGITAL_OK
      );
    return event(
      categories.TRAMITES,
      action.payload.isPublic
        ? `${actions.GENERACION_TRAMITE_SIN_LOGIN} ${action.payload.formName} ${WITHOUT_LOGIN}`
        : `${actions.GENERACION_TRAMITE} ${action.payload.formName}`,
      action.payload.isPublic ? labels.GENERACION_TRAMITE_SIN_LOGIN_OK : labels.GENERACION_TRAMITE_OK
    );
  }
  return null;
};

const submitFormFailure = action => {
  if (action.payload.formType === 'procedureSurvey' || action.payload.formType === 'paymentSurvey') {
    return event(categories.ENCUESTAS, actions.ERROR_AL_GENERAR_ENCUESTA);
  }
  if (action.payload.formType === 'claim') {
    return event(
      categories.RECLAMOS,
      action.payload.isPublic
        ? actions.GENERACION_RECLAMO_ERRONEO_SIN_LOGIN
        : actions.GENERACION_RECLAMO_ERRONEO,
      `${
        action.payload.isPublic ? labels.GENERACION_RECLAMO_SIN_LOGIN_ERROR : labels.GENERACION_RECLAMO_ERROR
      } ${action.payload.formName} | ${action.payload.error}`
    );
  }
  if (action.payload.formType === 'procedure') {
    return event(
      categories.TRAMITES,
      action.payload.isPublic
        ? `${actions.GENERACION_TRAMITE_ERRONEO_SIN_LOGIN} ${action.payload.formName} ${WITHOUT_LOGIN}`
        : `${actions.GENERACION_TRAMITE_ERRONEO} ${action.payload.formName}`,
      `${
        action.payload.isPublic ? labels.GENERACION_TRAMITE_SIN_LOGIN_ERROR : labels.GENERACION_TRAMITE_ERROR
      } ${action.payload.error}`
    );
  }
  return null;
};

const changeExpirationDateSuccess = () =>
  event(categories.TRAMITES, actions.CAMBIO_FECHA_VENCIMIENTO, labels.CAMBIO_FECHA_VENCIMIENTO_OK);

const changeExpirationDateFailure = action =>
  event(
    categories.TRAMITES,
    actions.CAMBIO_FECHA_VENCIMIENTO_ERRONEO,
    `${labels.CAMBIO_FECHA_VENCIMIENTO_ERROR} ${action.payload.error}`
  );

const verificationCodeSent = () =>
  event(
    categories.REGISTRACIONES,
    actions.SOLICITUD_CODIGO_VERIFICACION,
    labels.SOLICITUD_CODIGO_VERIFICACION_OK
  );

const validateSocialRateSuccess = action =>
  event(
    categories.CONSULTAS,
    action.payload.isPublic ? actions.CONSULTAR_TARIFA_SOCIAL_SIN_LOGIN : actions.CONSULTAR_TARIFA_SOCIAL,
    action.payload.isPublic ? labels.CONSULTAR_TARIFA_SOCIAL_SIN_LOGIN_OK : labels.CONSULTAR_TARIFA_SOCIAL_OK
  );

const voucherExchangeSuccess = action =>
  event(categories.RECOMPENSAS, actions.CANJE_VOUCHER, `${labels.CANJE_VOUCHER_OK} ${action.payload}`);

const getAccountBalanceSuccess = () =>
  event(categories.CONSULTAS, actions.CONSULTA_SALDO, labels.CONSULTA_SALDO_OK);

const getLastBillInformationSuccess = () =>
  event(categories.CONSULTAS, actions.CONSULTA_ULTIMA_FACTURA, labels.CONSULTA_ULTIMA_FACTURA_OK);

const updateUserProfileSuccess = () =>
  event(categories.CONTACTO, actions.ACTUALIZACION_DATOS_USUARIO, labels.ACTUALIZACION_DATOS_USUARIO_OK);

const updateUserProfileFailure = action =>
  event(
    categories.CONTACTO,
    actions.ACTUALIZACION_DATOS_USUARIO_ERRONEO,
    `${labels.ACTUALIZACION_DATOS_USUARIO_ERROR} ${action.payload}`
  );

const getAccountNotificationsSuccess = () =>
  event(categories.CONSULTAS, actions.CONSULTA_NOTIFICACIONES, labels.CONSULTA_NOTIFICACIONES_OK);

const getPrintedBillSuccess = () =>
  event(categories.FACTURACION, actions.DESCARGA_FACTURA, labels.DESCARGA_FACTURA_OK);

const getPrintedBillFailure = action =>
  event(
    categories.FACTURACION,
    actions.DESCARGA_FACTURA_ERROR,
    `${labels.DESCARGA_FACTURA_ERROR} ${action.payload.error}`
  );

const getMultipleBillsSuccess = () =>
  event(categories.FACTURACION, actions.DESCARGA_MASIVA_FACTURAS, labels.DESCARGA_MASIVA_FACTURAS_OK);

const getMultipleBillsFailure = action =>
  event(
    categories.FACTURACION,
    actions.DESCARGA_MASIVA_FACTURAS_ERROR,
    `${labels.DESCARGA_MASIVA_FACTURAS_ERROR} ${action.payload.error}`
  );

const acceptServiceAgreementSuccess = () =>
  event(categories.FACTURACION, actions.ADHESION_FACTURA_DIGITAL, labels.ADHESION_FACTURA_DIGITAL_OK);

const tipToggleLikeSuccess = action => {
  const tip = {};
  if (action.payload.like) {
    tip.action = actions.MARCAR_TIP_FAVORITO;
    tip.label = labels.MARCAR_TIP_FAVORITO_OK;
  } else {
    tip.action = actions.DESMARCAR_TIP_FAVORITO;
    tip.label = labels.DESMARCAR_TIP_FAVORITO_OK;
  }
  return event(categories.ASESOR_ENERGETICO, tip.action, `${tip.label} ${action.payload.tip.description}`);
};

const tipShare = action =>
  event(
    categories.ASESOR_ENERGETICO,
    actions.COMPARTIR_TIP,
    `${labels.COMPARTIR_TIP_OK} ${action.payload.description}`
  );

const createNewServiceSuccess = () =>
  event(categories.TRAMITES, actions.INICIO_ALTA_SUMINISTRO, labels.INICIO_ALTA_SUMINISTRO_OK);

const createHolderChangeSuccess = () =>
  event(categories.TRAMITES, actions.INICIO_CAMBIO_TITULARIDAD, labels.INICIO_CAMBIO_TITULARIDAD_OK);

const createTerminateRequestSuccess = () =>
  event(categories.TRAMITES, actions.INICIO_BAJA_SUMINISTRO, labels.INICIO_BAJA_SUMINISTRO_OK);

const createNewServiceFailure = () =>
  event(categories.TRAMITES, actions.INICIO_ALTA_SUMINISTRO_ERRONEO, labels.INICIO_ALTA_SUMINISTRO_ERROR);

const createHolderChangeFailure = () =>
  event(
    categories.TRAMITES,
    actions.INICIO_CAMBIO_TITULARIDAD_ERRONEO,
    labels.INICIO_CAMBIO_TITULARIDAD_ERROR
  );

const createTerminateRequestFailure = () =>
  event(categories.TRAMITES, actions.INICIO_BAJA_SUMINISTRO_ERRONEO, labels.INICIO_BAJA_SUMINISTRO_ERROR);

const getSurveyformSuccess = () => event(categories.ENCUESTAS, actions.DESPLIEGUE_DE_ENCUESTA);

const eventDefinition = {
  [authActions.RECOVERY_PASSWORD_REQUEST_SUCCESS]: recoveryPasswordRequestSuccess,
  [authActions.RECOVERY_PASSWORD_SUCCESS]: recoveryPasswordSuccess,
  [authActions.CHANGE_PASSWORD_SUCCESS]: changePasswordSuccess,
  [authActions.SIGN_UP_SUCCESS]: signUpSuccess,
  [authActions.VALIDATE_EMAIL_SUCCESS]: validateEmailSuccess,
  [authActions.SIGN_IN_SUCCESS]: signInSuccess,
  [authActions.SIGN_IN_FAILURE]: signInFailure,
  [authActions.SIGN_OUT]: signOut,
  [authActions.GET_IDENTITY_VALIDATION_FORM_SUCCESS]: getIdentityValidationFormSuccess,
  [authActions.VALIDATE_IDENTITY_ANSWERS_SUCCESS]: validateIdentityAnswersSuccess,
  [authActions.VALIDATE_IDENTITY_ANSWERS_FAILURE]: validateIdentityAnswersFailure,
  [authActions.VERIFICATION_CODE_SENT]: verificationCodeSent,
  [authActions.SIGN_UP]: verificationCodeSent,
  [accountActions.DELETE_ACCOUNT_SUCCESS]: deleteAccountSuccess,
  [paymentActions.GET_PAYMENT_DOCUMENT_SUCCESS]: getPaymentDocumentSuccess,
  [paymentActions.GET_PAYMENT_DOCUMENT_PDF_SUCCESS]: getPaymentDocumentSuccess,
  [BillActions.GET_PAYMENT_DOCUMENT_SUCCESS]: getPublicPaymentDocumentSuccess,
  [paymentActions.GET_MULTIPLE_PAYMENT_DOCUMENTS_SUCCESS]: getMultiplePaymentDocumentsSuccess,
  [paymentActions.PAYMENT_REQUEST_FAILURE]: paymentRequestFailure,
  [paymentActions.PAYMENT_REQUEST_SUCCESS]: paymentRequestSuccess,
  [userFormActions.CANCELATION_SUCCESS]: cancelationSuccess,
  [userFormActions.REITERATION_SUCCESS]: reiterationSuccess,
  [userFormActions.SUBMIT_FORM_SUCCESS]: submitFormSuccess,
  [userFormActions.SUBMIT_FORM_FAILURE]: submitFormFailure,
  [accountActions.CHANGE_EXPIRATION_DATE_SUCCESS]: changeExpirationDateSuccess,
  [accountActions.CHANGE_EXPIRATION_DATE_FAILURE]: changeExpirationDateFailure,
  [accountActions.VALIDATE_SOCIAL_RATE_SUCCESS]: validateSocialRateSuccess,
  [voucherActions.VOUCHER_EXCHANGE_SUCCESS]: voucherExchangeSuccess,
  [accountActions.GET_ACCOUNT_BALANCE_SUCCESS]: getAccountBalanceSuccess,
  [BillActions.GET_LAST_BILL_INFORMATION_SUCCESS]: getLastBillInformationSuccess,
  [BillActions.GET_PRINTED_BILL_SUCCESS]: getPrintedBillSuccess,
  [BillActions.GET_PRINTED_BILL_FAILURE]: getPrintedBillFailure,
  [BillActions.GET_PRINTED_BILL_PDF_SUCCESS]: getPrintedBillSuccess,
  [BillActions.GET_PRINTED_BILL_PDF_FAILURE]: getPrintedBillFailure,
  [BillActions.GET_MULTIPLE_BILLS_SUCCESS]: getMultipleBillsSuccess,
  [BillActions.GET_MULTIPLE_BILLS_FAILURE]: getMultipleBillsFailure,
  [authActions.UPDATE_USER_PROFILE_SUCCESS]: updateUserProfileSuccess,
  [authActions.UPDATE_USER_PROFILE_FAILURE]: updateUserProfileFailure,
  [notificationActions.GET_ACCOUNT_NOTIFICATIONS_SUCCESS]: getAccountNotificationsSuccess,
  [accountActions.ACCEPT_SERVICE_AGREEMENT_SUCCESS]: acceptServiceAgreementSuccess,
  [tipActions.TIP_TOGGLE_LIKE_SUCCESS]: tipToggleLikeSuccess,
  [tipActions.TIP_SHARE]: tipShare,
  [customProceduresActions.CREATE_NEW_SERVICE_SUCCESS]: createNewServiceSuccess,
  [customProceduresActions.CREATE_HOLDER_CHANGE_SUCCESS]: createHolderChangeSuccess,
  [customProceduresActions.CREATE_TERMINATE_REQUEST_SUCCESS]: createTerminateRequestSuccess,
  [customProceduresActions.CREATE_NEW_SERVICE_FAILURE]: createNewServiceFailure,
  [customProceduresActions.CREATE_HOLDER_CHANGE_FAILURE]: createHolderChangeFailure,
  [customProceduresActions.CREATE_TERMINATE_REQUEST_FAILURE]: createTerminateRequestFailure,
  [surveyActions.GET_SURVEY_FORM_SUCCESS]: getSurveyformSuccess
};

export default eventDefinition;
